import React from "react";
import { dateInputFormatOf,dateTimeInputFormatOf } from "./format";
import type { UnitsSettings } from "./units";
import { useEffectiveUnits } from "./useEffectiveUnits";
import {UnitsProvider} from "../../../../../../shared/frontend/units/UnitsProvider";

export type DateInputFormatContextValue = {
  dateInputFormat: string;
  dateInputFormats: string[];
  dateTimeInputFormat: string;
};

const defaultValue: DateInputFormatContextValue = {
  dateInputFormat: dateInputFormatOf(undefined),
  dateInputFormats: dateInputFormatsWithCompact(dateInputFormatOf(undefined)),
  dateTimeInputFormat: dateTimeInputFormatOf(undefined)
};

const DateInputFormatContext = React.createContext<DateInputFormatContextValue>(defaultValue);

/**
 * Liefert Datumsformate für Ant Design DatePicker/RangePicker abgestimmt auf die Konten-Units
 * (nicht nur UI-Sprache), konsistent zu formatDateOnly / Listen.
 */
export function DateInputFormatProvider({
  units: unitsProp,
  children
}: {
  units?: UnitsSettings;
  children: React.ReactNode;
}) {
  const effectiveUnits = useEffectiveUnits();
  const units = unitsProp ?? effectiveUnits;
  const value = React.useMemo(() => {
    const dateInputFormat = dateInputFormatOf(units);
    return {
      dateInputFormat,
      dateInputFormats: dateInputFormatsWithCompact(dateInputFormat),
      dateTimeInputFormat: dateTimeInputFormatOf(units)
    };
  }, [units]);

  return <UnitsProvider value={units}><DateInputFormatContext.Provider value={value}>{children}</DateInputFormatContext.Provider></UnitsProvider>;
}

export function useDateInputFormatContext(): DateInputFormatContextValue {
  return React.useContext(DateInputFormatContext);
}

export function dateInputFormatsWithCompact(format: string): string[] {
  const trimmed = (format || "").trim();
  if (!trimmed) return [];

  const compact = trimmed.replace(/[^A-Za-z]/g, "");
  const singleDigit = trimmed
    .replace(/DD/g, "D")
    .replace(/MM/g, "M");
  const separatorVariants = [".", "/", "-", " "].flatMap((separator) => [
    trimmed.replace(/[^A-Za-z]+/g, separator),
    singleDigit.replace(/[^A-Za-z]+/g, separator)
  ]);
  return Array.from(new Set([
    trimmed,
    compact,
    singleDigit,
    ...separatorVariants,
    "YYYY-MM-DD",
    "YYYY/MM/DD",
    "YYYYMMDD"
  ].filter(Boolean)));
}

export function useDateInputFormat(): string[] {
  return useDateInputFormatContext().dateInputFormats;
}

export function useDateInputFormats(): string[] {
  return useDateInputFormatContext().dateInputFormats;
}

export function useDateTimeInputFormat(): string {
  return useDateInputFormatContext().dateTimeInputFormat;
}
